import { init as initFullStory } from "@fullstory/browser";
import { StyledEngineProvider, ThemeProvider, createTheme, responsiveFontSizes } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import * as Sentry from "@sentry/react";
import ErrorPage from "_shared/_errors/_views/error-page";
import { SystemNotificationBannerProvider } from "_shared/_providers/banner/system-notification-banner-provider";
import ConfirmDialogProvider from "_shared/_providers/dialogs/confirm/confirm-dialog";
import DialogProvider from "_shared/_providers/dialogs/dialog";
import ImagePreviewDialogProvider from "_shared/_providers/dialogs/image-preview/image-preview-dialog";
import attorneyHubStore from "_shared/_stores/redux-store";
import { enableInertModalHiding } from "_shared/_utilities/a11y/inert-modal-hiding";
import { ENVIRONMENT, IS_DEV, IS_E2E } from "_shared/_utilities/meta";
import Dashboard from "_shared/_views/dashboards/dashboard";
import dashboardLoader from "_shared/_views/dashboards/dashboard-loader";
import invitationLoader from "_shared/_views/loaders/invitation";
import marketplaceLoader from "_shared/_views/loaders/marketplace";
import marketplaceProfileLoader from "_shared/_views/loaders/marketplace-profile";
import marketplaceResultProfileLoader from "_shared/_views/loaders/marketplace-result-profile";
import networkLoader from "_shared/_views/loaders/network";
import rootLoader from "_shared/_views/loaders/root";
import superUserLoader from "_shared/_views/loaders/super-users";
import teamsLoader from "_shared/_views/loaders/teams";
import Main from "_shared/_views/main";
import RouteAccessGuard from "_shared/_views/route-access-guard";
import Calendar from "calendar/_views/calendar";
import Teams from "_shared/teams/teams-page";
import Contracts from "documents/contract-documents/_views/contracts-page";
import "./index.css";
import EntityAdministration from "legal-entities/administrators/_views/administration-page";
import PermissionsPage from "permissions/_views/tables/permissions-page";
import entityAdministratorLoader from "legal-entities/administrators/_views/administrator-loader";
import EntityAgreementPage from "legal-entities/agreements/_views/entity-agreement-page";
import UserEntityMemberInvitationAcceptancePage from "legal-entities/invitations/member-invitations/email-based/_views/user-entity-member-invitation-acceptance-page";
import OpenEntityMemberInvitationPage from "legal-entities/invitations/member-invitations/open/_views/open-entity-member-invitation-acceptance-page";
import EntityOfficerInvitationPage from "legal-entities/invitations/officer-invitations/_views/entity-officer-invitation-page";
import TeamLeaderRosterPage from "legal-entities/invitations/bulk/_views/team-leader-roster-page";
import TeamMemberRosterPage from "legal-entities/invitations/bulk/_views/team-member-roster-page";
import FirmOnboardingStatusPage from "legal-entities/vendor-rollouts/_views/firm-onboarding-status-page";
import ClientProposalTemplatesPage from "work/client-proposal-templates/_views/client-proposal-templates-page";
import MarketplacePage from "marketplace/_views/marketplace-page";
import ManageProfiles from "marketplace/_views/profiles/profiles-page";
import Bookmarks from "marketplace/bookmarks/_views/bookmarks-page";
import bookmarksLoader from "marketplace/bookmarks/_views/loaders/bookmarks-loader";
import TeamInvitationPage from "marketplace/teams/invitations/_views/team-invitation-page";
import CommunicationsPage from "notifications/_views/communications-page";
import communicationsLoader from "notifications/_views/loader/communications-loader";
import { SnackbarProvider } from "notistack";
import React from "react";
import { createRoot, type Root } from "react-dom/client";
import { Provider as ReduxProvider } from "react-redux";
import { createBrowserRouter, Outlet, RouterProvider } from "react-router";

import { RequireSession, SessionProvider } from "users/__session/session-context";
import LoginPage from "users/auth/_views/login-page";
import Network from "users/networking/_views/network-page";
import SuperUserDashboard from "users/super-users/_views/super-user-administration-panel";
import FeeSchedulesPage from "work/fee-schedule-templates/_views/pages/fee-schedules-page";
import {
  activeFeeScheduleLoader,
  archivedFeeScheduleLoader,
  feeScheduleLoader,
  templateFeeScheduleLoader,
} from "work/fee-schedule-templates/_views/pages/loaders/fee-schedule-loader";
import ActiveProposalPage from "work/proposals/_views/active/active-proposal-page";
import ArchivedProposalPage from "work/proposals/_views/archive/archived-proposal-page";
import DraftProposalPage from "work/proposals/_views/drafts/draft-proposal-page";
import {
  activeProposalLoader,
  archivedProposalLoader,
  draftProposalLoader,
  proposalLoader,
  withDefaultTab,
} from "work/proposals/_views/loaders/proposal-loader";
import ProposalsPage from "work/proposals/_views/proposals-page";
import TeamTemplatePage from "work/team-templates/_views/team-template-page";
import ViewWorkAgreementPage from "work/work-agreements/_views/view/view-work-agreement-page";
import {
  activeWorkAgreementLoader,
  archivedWorkAgreementLoader,
  workLoader,
} from "work/work-agreements/_views/loaders/work-agreement-loader";
import WorkAgreementsPage from "work/work-agreements/_views/work-agreements-page";
import FeeScheduleTemplatePage from "_shared/fee-schedules/categories/_views/templates/fee-schedule-template-page";
import HelpPage from "_shared/_views/help/help-page";
import KnowledgeBasePage from "knowledge-base/_views/knowledge-base-page";
import { TutorialProvider } from "_shared/tutorials/tutorial-provider";

declare global {
  interface Window {
    __attorneyHubBootstrap?: {
      fullStoryInitialized: boolean;
      sentryInitialized: boolean;
      root?: Root;
    };
  }
}

const bootstrapState = (window.__attorneyHubBootstrap ??= {
  fullStoryInitialized: false,
  sentryInitialized: false,
  root: undefined,
});
if (IS_E2E) document.documentElement.classList.add("e2e");

// FullStory in devMode does nothing except announce, on every boot, that it will not record. Skip it
// locally rather than paying for a console warning that carries no information.
if (!IS_E2E && !IS_DEV && !bootstrapState.fullStoryInitialized) {
  initFullStory({ orgId: "o-1M2PCX-na1" });
  bootstrapState.fullStoryInitialized = true;
}

if (!IS_E2E && !bootstrapState.sentryInitialized) {
  Sentry.init({
    dsn: "https://9b4b0f7cb45a485ea5dcef37b8da09d7@o1250895.ingest.sentry.io/6415866",
    enabled: !IS_DEV,
    environment: ENVIRONMENT,
    tracesSampleRate: 1,
  });
  bootstrapState.sentryInitialized = true;
}

// Backport MUI's `inert`-based background hiding so stacked modals/popovers
// never leave focus inside an `aria-hidden` ancestor (Chrome a11y violation).
enableInertModalHiding();

let theme = createTheme();
theme = responsiveFontSizes(theme);

const RequireSessionLayout = () => (
  <RequireSession>
    <Outlet />
  </RequireSession>
);

const DialogLayout = () => (
  <DialogProvider>
    <TutorialProvider>
      <Outlet />
    </TutorialProvider>
  </DialogProvider>
);

const routes = [
  {
    path: "entity-officer-invitation/:invitationId",
    element: <EntityOfficerInvitationPage />,
    errorElement: <ErrorPage />,
    loader: invitationLoader,
  },
  {
    path: "entity-member-invitation/:invitationId",
    element: <OpenEntityMemberInvitationPage />,
    errorElement: <ErrorPage />,
    loader: invitationLoader,
  },
  {
    path: "user-member-invitation/:invitationId",
    element: <UserEntityMemberInvitationAcceptancePage />,
    errorElement: <ErrorPage />,
    loader: invitationLoader,
  },
  {
    path: "login",
    element: <LoginPage />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/",
    element: <RouteAccessGuard><Main /></RouteAccessGuard>,
    errorElement: <ErrorPage />,
    loader: rootLoader,
    children: [
      {
        element: <RequireSessionLayout />,
        errorElement: <ErrorPage />,
        children: [
          {
            path: "dashboard",
            element: <Dashboard />,
            loader: dashboardLoader,
          },

          {
            path: "communications",
            element: <CommunicationsPage />,
            loader: communicationsLoader,
          },
          {
            path: "communications/:pageTab",
            element: <CommunicationsPage />,
            loader: communicationsLoader,
          },
          {
            path: "communications/:pageTab/:action",
            element: <CommunicationsPage />,
            loader: communicationsLoader,
          },
          {
            path: "communications/:pageTab/:action/:id",
            element: <CommunicationsPage />,
            loader: communicationsLoader,
          },

          {
            path: "contracts",
            element: <Contracts />,
          },
          {
            path: "contracts/:pageTab",
            element: <Contracts />,
          },

          {
            path: "work",
            children: [
              {
                index: true,
                element: <WorkAgreementsPage />,
                loader: withDefaultTab("active", workLoader),
              },
              {
                path: ":pageTab",
                element: <WorkAgreementsPage />,
                loader: workLoader,
              },

              {
                path: "active/:action/:id",
                element: <ViewWorkAgreementPage />,
                loader: withDefaultTab("details", activeWorkAgreementLoader),
              },
              {
                path: "active/:action/:id/:pageTab",
                element: <ViewWorkAgreementPage />,
                loader: activeWorkAgreementLoader,
              },

              {
                path: "archived/:action/:id",
                element: <ViewWorkAgreementPage />,
                loader: withDefaultTab("details", archivedWorkAgreementLoader),
              },
              {
                path: "archived/:action/:id/:pageTab",
                element: <ViewWorkAgreementPage />,
                loader: archivedWorkAgreementLoader,
              },
            ],
          },

          {
            path: "proposals",
            children: [
              {
                index: true,
                element: <ProposalsPage />,
                loader: withDefaultTab("active", proposalLoader),
              },
              {
                path: ":pageTab",
                element: <ProposalsPage />,
                loader: proposalLoader,
              },

              {
                path: "drafts/create",
                element: <DraftProposalPage />,
                loader: withDefaultTab("details", draftProposalLoader),
              },
              {
                path: "drafts/create/:pageTab",
                element: <DraftProposalPage />,
                loader: draftProposalLoader,
              },

              {
                path: "drafts/:action/:id",
                element: <DraftProposalPage />,
                loader: withDefaultTab("details", draftProposalLoader),
              },
              {
                path: "drafts/:action/:id/:pageTab",
                element: <DraftProposalPage />,
                loader: draftProposalLoader,
              },

              {
                path: "active/:action/:id",
                element: <ActiveProposalPage />,
                loader: withDefaultTab("details", activeProposalLoader),
              },
              {
                path: "active/:action/:id/:pageTab",
                element: <ActiveProposalPage />,
                loader: activeProposalLoader,
              },

              {
                path: "archived/:action/:id",
                element: <ArchivedProposalPage />,
                loader: withDefaultTab("details", archivedProposalLoader),
              },
              {
                path: "archived/:action/:id/:pageTab",
                element: <ArchivedProposalPage />,
                loader: archivedProposalLoader,
              },
            ],
          },

          {
            path: "teams",
            element: <Teams />,
            loader: teamsLoader,
          },
          {
            path: "teams/:pageTab",
            element: <Teams />,
            loader: teamsLoader,
          },
          {
            path: "teams/templates/create",
            element: <TeamTemplatePage />,
            loader: teamsLoader,
          },
          {
            path: "teams/templates/:action/:id",
            element: <TeamTemplatePage />,
            loader: teamsLoader,
          },
          {
            path: "teams/archived/:action/:id",
            element: <TeamTemplatePage />,
            loader: teamsLoader,
          },

          {
            path: "fee-schedules",
            children: [
              {
                index: true,
                element: <FeeSchedulesPage />,
                loader: withDefaultTab("templates", feeScheduleLoader),
              },
              {
                path: ":pageTab",
                element: <FeeSchedulesPage />,
                loader: feeScheduleLoader,
              },
              {
                path: "templates/create",
                element: <FeeScheduleTemplatePage />,
                loader: templateFeeScheduleLoader,
              },
              {
                path: "templates/:action/:id",
                element: <FeeScheduleTemplatePage />,
                loader: templateFeeScheduleLoader,
              },
              {
                path: "active/:action/:id",
                element: <FeeScheduleTemplatePage />,
                loader: activeFeeScheduleLoader,
              },
              {
                path: "archived/:action/:id",
                element: <FeeScheduleTemplatePage />,
                loader: archivedFeeScheduleLoader,
              },
            ],
          },

          {
            path: "marketplace/vendor-team-invitation/:invitationId",
            element: <TeamInvitationPage />,
          },

          {
            path: "marketplace/profiles",
            element: <ManageProfiles />,
            loader: marketplaceProfileLoader,
          },
          {
            path: "marketplace/profiles/:pageTab",
            element: <ManageProfiles />,
            loader: marketplaceProfileLoader,
          },
          {
            path: "marketplace/profiles/:pageTab/:action",
            element: <ManageProfiles />,
            loader: marketplaceProfileLoader,
          },
          {
            path: "marketplace/profiles/:pageTab/:action/:id",
            element: <ManageProfiles />,
            loader: marketplaceProfileLoader,
          },

          {
            path: "marketplace/bookmarks",
            element: <Bookmarks />,
            loader: bookmarksLoader,
          },
          {
            path: "marketplace/bookmarks/:pageTab",
            element: <Bookmarks />,
            loader: bookmarksLoader,
          },

          {
            path: "marketplace",
            element: <MarketplacePage />,
            loader: marketplaceLoader,
          },
          {
            path: "marketplace/:pageTab",
            element: <MarketplacePage />,
            loader: marketplaceResultProfileLoader,
          },
          {
            path: "marketplace/:pageTab/:id",
            element: <MarketplacePage />,
            loader: marketplaceResultProfileLoader,
          },

          {
            path: "network",
            element: <Network />,
            loader: networkLoader,
          },
          {
            path: "network/:pageTab",
            element: <Network />,
            loader: networkLoader,
          },
          {
            path: "network/:pageTab/:action",
            element: <Network />,
            loader: networkLoader,
          },
          {
            path: "network/:pageTab/:action/:id",
            element: <Network />,
            loader: networkLoader,
          },

          {
            path: "calendar",
            element: <Calendar />,
          },

          {
            path: "organization",
            element: <EntityAdministration />,
            loader: entityAdministratorLoader,
          },
          {
            path: "organization/:pageTab",
            element: <EntityAdministration />,
            loader: entityAdministratorLoader,
          },
          {
            path: "organization/:pageTab/:action",
            element: <EntityAdministration />,
            loader: entityAdministratorLoader,
          },
          {
            path: "organization/:pageTab/:action/:id",
            element: <EntityAdministration />,
            loader: entityAdministratorLoader,
          },
          {
            path: "permissions",
            element: <PermissionsPage />,
            loader: entityAdministratorLoader,
          },
          {
            path: "firm-onboarding",
            element: <FirmOnboardingStatusPage />,
          },
          {
            path: "client-proposal-templates",
            element: <ClientProposalTemplatesPage />,
          },
          {
            path: "invite-colleagues",
            element: <TeamMemberRosterPage />,
          },
          {
            // The same page, named by the firm whose roster it is. This is where the prompt raised
            // on the Attorney Hub proposal's hire lands: notification routes substitute a topic id
            // into the path, and this notification's topic is the firm.
            path: "invite-colleagues/:entityId",
            element: <TeamMemberRosterPage />,
          },
          {
            path: "invite-team-leaders",
            element: <TeamLeaderRosterPage />,
          },
          {
            path: "super-user",
            element: <SuperUserDashboard />,
            loader: superUserLoader,
          },
          {
            path: "super-user/:pageTab",
            element: <SuperUserDashboard />,
            loader: superUserLoader,
          },
          {
            path: "super-user/:pageTab/:profileType/:id",
            element: <SuperUserDashboard />,
            loader: superUserLoader,
          },

          {
            path: "knowledge-base",
            element: <KnowledgeBasePage />,
          },
          {
            // Ahead of the :slug route, and "attorney-hub" is a reserved slug on the server, so an
            // article can never be created that this address would otherwise shadow.
            path: "knowledge-base/attorney-hub",
            element: <KnowledgeBasePage publicLibraryOnly />,
          },
          {
            // Attorney Hub's articles have addresses of their own. Without these, opening one had
            // nowhere to go but the entity route, which reads a different knowledge base entirely.
            path: "knowledge-base/attorney-hub/:slug",
            element: <KnowledgeBasePage publicLibraryOnly />,
          },
          {
            path: "knowledge-base/attorney-hub/:slug/edit",
            element: <KnowledgeBasePage publicLibraryOnly />,
          },
          {
            path: "knowledge-base/:slug",
            element: <KnowledgeBasePage />,
          },
          {
            path: "knowledge-base/:slug/edit",
            element: <KnowledgeBasePage />,
          },
          {
            path: "help",
            element: <HelpPage />,
          },
          {
            path: "entity-agreements/:id",
            element: <EntityAgreementPage />,
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <ErrorPage error={{ status: 404, statusText: "Page Not Found" }} />,
  },
];

const router = createBrowserRouter([
  {
    element: <DialogLayout />,
    children: routes,
  },
]);

const container = document.getElementById("root");
if (!container) {
  throw new Error("Root element not found");
}

const root = (bootstrapState.root ??= createRoot(container));
const application = (
  <ReduxProvider store={attorneyHubStore}>
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <SystemNotificationBannerProvider>
            <SnackbarProvider maxSnack={3}>
              <SessionProvider>
                <ConfirmDialogProvider>
                  <ImagePreviewDialogProvider>
                    <RouterProvider router={router} />
                  </ImagePreviewDialogProvider>
                </ConfirmDialogProvider>
              </SessionProvider>
            </SnackbarProvider>
          </SystemNotificationBannerProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  </ReduxProvider>
);
root.render(IS_E2E ? application : <React.StrictMode>{application}</React.StrictMode>);
